import { Middleware } from "redux";
import { io, Socket } from "socket.io-client";

import {
  MessagesConversation,
  SendMessagePayload,
  CreateConversationPayload,
  GetConversationPayload,
  ReceiveMessage,
  MarkConversationAsReadPayload,
} from "slices/messages";

import * as messagesActions from "slices/messages";

import addSocketListeners from "./addSocketListeners";
import socketEmit from "./socketEmit";
import { MessagesEvent } from "./types";

let socket: Socket;

const messagesMiddleware: Middleware = (store) => {
  return (next) => (action) => {
    const isConnectionEstablished =
      socket && store.getState().messages.isConnected;

    if (messagesActions.connectionConnect.match(action)) {
      const commsAuthToken = store.getState().auth.comms_auth_token;

      socket = io(process.env.REACT_APP_COMMS_URL!, {
        extraHeaders: {
          authorization: commsAuthToken,
          user: "owner",
        },
      });

      addSocketListeners(store, socket);
    }

    if (
      messagesActions.getAllConversations.match(action) &&
      isConnectionEstablished
    ) {
      socketEmit<{}, MessagesConversation[]>(
        socket,
        MessagesEvent.GetAllConversations,
        {},
      ).then(({ response, error }) => {
        if (response) {
          store.dispatch(
            messagesActions.receiveAllConversations({
              conversations: response || [],
            }),
          );
        }
        if (error) {
          store.dispatch(
            messagesActions.setError({
              source: MessagesEvent.GetAllConversations,
            }),
          );
          try {
            window.newrelic.noticeError(
              `Failed to get conversation list - ${error}`,
            );
          } catch {}
        }
      });
    }

    if (
      messagesActions.getConversation.match(action) &&
      isConnectionEstablished
    ) {
      socketEmit<GetConversationPayload, MessagesConversation>(
        socket,
        MessagesEvent.GetConversation,
        action.payload,
      ).then(({ response, error }) => {
        if (response) {
          store.dispatch(
            messagesActions.receiveConversation({ conversation: response }),
          );
        }
        if (error) {
          store.dispatch(
            messagesActions.setError({ source: MessagesEvent.GetConversation }),
          );
          try {
            window.newrelic.noticeError(
              `Failed to get conversation - ${error}`,
            );
          } catch {}
        }
      });
    }

    if (messagesActions.sendMessage.match(action) && isConnectionEstablished) {
      socketEmit<SendMessagePayload, ReceiveMessage>(
        socket,
        MessagesEvent.SendMessage,
        action.payload,
      ).then(({ error }) => {
        if (error) {
          // @TODO: Create an error message manager to convert server errors into customer-facing error.
          store.dispatch(
            messagesActions.setError({
              source: MessagesEvent.SendMessage,
              message: "Something went wrong when sending your message",
            }),
          );
          try {
            window.newrelic.noticeError(`Failed to send message - ${error}`);
          } catch {}
        }
      });
    }

    if (
      messagesActions.createConversation.match(action) &&
      isConnectionEstablished
    ) {
      socketEmit<CreateConversationPayload, MessagesConversation>(
        socket,
        MessagesEvent.CreateConversation,
        action.payload,
      ).then(({ error }) => {
        if (error) {
          // @TODO: Create an error message manager to convert server errors into customer-facing error.
          store.dispatch(
            messagesActions.setError({
              source: MessagesEvent.CreateConversation,
              message: "Something went wrong when creating your conversation",
            }),
          );
          try {
            window.newrelic.noticeError(
              `Failed to create conversation - ${error}`,
            );
          } catch {}
        }
      });
    }

    if (
      messagesActions.markConversationAsRead.match(action) &&
      isConnectionEstablished
    ) {
      socketEmit<MarkConversationAsReadPayload, MessagesConversation>(
        socket,
        MessagesEvent.MarkConversationAsRead,
        action.payload,
      );
    }

    next(action);
  };
};

export default messagesMiddleware;
